/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import {
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { defaultValue } from './ExpressionBuilderField';
import { baseFieldProps } from '../TextField/BaseField';

const ExpressionBuilderFieldManager: ComponentManagerFn<
  'ExpressionBuilderField'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'ExpressionBuilderField'>(
      platform,
      'ExpressionBuilderField',
    ),
    type: 'ExpressionBuilderField',
    name: 'Expression Field',
    defaultValue,
    props: {
      ...baseFieldProps,
      viewAttribute: {
        label: 'View Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String'],
        preventMultiEdit: true,
      } as ViewAttributeChooserProp,
    },
    getDisplayText: (item) => item.props.viewAttribute ?? '',
    category: 'Forms',
    icon: { iconName: 'sigma' },
    description: `**Overview**<br/>
The Expression Field component is a specialized input control that allows end-users to construct logical expressions, formulas, or calculations at runtime. Unlike a standard text field, this component is designed to handle syntax typically used for defining business rules or dynamic values.
Use this component when you need to empower your application users to define their own logic—such as creating custom pricing formulas, setting up alert conditions, or defining calculated columns—without needing to write actual code.

**1. Data Binding**<br/>
This component binds to a text field in your database to store the raw formula string.
* **View Attribute:** Select the Data Store field to read from and write to.
* **Supported Type:** **String**.
* **Storage Behavior:** It saves the **definition** of the logic (e.g., "Price * Quantity") rather than the calculated result.

**2. Configuration & Styling**<br/>
The Expression Field inherits standard form field properties (**baseFieldProps**), allowing it to blend seamlessly with other inputs in your form.
* **Label:** The text label displayed above or beside the field.
* **Placeholder:** Hint text displayed inside the field when empty (e.g., "(A + B) / C").
* **Helper Text:** Explanatory text below the field to guide users on valid syntax.
* **Read-Only/Disabled:** Controls whether the user can modify the expression.

**3. Best Use Cases**<br/>
* **Dynamic Pricing Engines:** Allowing an admin to define a pricing formula for a product (e.g., \`BasePrice * 1.2 + Tax\`).
* **Custom Reporting:** Letting users define their own calculated columns in a report generator.
* **Workflow Rules:** Enabling users to write simple boolean logic for triggers (e.g., \`Amount > 1000 AND Status == 'Pending'\`).
* **Commission Structures:** Defining unique commission logic for different sales agents.`,
  };
};

export default ExpressionBuilderFieldManager;
